/**
 * Fetch and update user_events for explore Tags rail (no Tagger localStorage).
 */
import { apiEndpoints } from '@config/env';
import { getData, putData } from '@utils/global';
import { debug, warn, error as logError } from '@utils/console';

/** Dispatched on `window` after the explore map creates a user event; TagsExplorePanel listens to refetch. */
export const EXPLORE_USER_EVENTS_CHANGED_EVENT = 'explore-user-events-changed' as const;

export interface ExploreUserEventRow {
  user_event_id: number;
  project_id: number;
  user_id: string | null;
  user_name?: string | null;
  date: string | null;
  focus_time: string | null;
  start_time: string | null;
  end_time: string | null;
  /** Canonical UTC instant from API (ISO with Z). Prefer for sort/seek/selection. */
  focus_time_utc?: string | null;
  start_time_utc?: string | null;
  end_time_utc?: string | null;
  event_type: string;
  comment: string;
  tags?: Record<string, unknown> | null;
  date_modified?: string | null;
}

export interface ExploreTagMergedRow {
  kind: 'user_event' | 'dataset_event';
  user_event_id: number | null;
  event_id: number | null;
  project_id: number;
  user_name?: string | null;
  date: string | null;
  /** Display-time strings in resolved explore timezone (backward compatible). */
  focus_time: string | null;
  start_time: string | null;
  end_time: string | null;
  /** Canonical UTC instants (ISO with Z) for sort/seek/selection. */
  focus_time_utc?: string | null;
  start_time_utc?: string | null;
  end_time_utc?: string | null;
  event_type: string;
  tags?: string | Record<string, unknown> | null;
  comment: string;
}

const ISO_WITH_TZ_SUFFIX_RE = /(z|[+-]\d{2}:?\d{2})$/i;

/** Parse canonical UTC ISO from API; returns null when missing or invalid. */
export function parseExploreUtcIso(iso: string | null | undefined): string | null {
  const raw = String(iso ?? '').trim();
  if (!raw) {return null;}
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) {return null;}
  return d.toISOString();
}

/**
 * Prefer API *_utc fields; fall back to display field when it already includes Z/offset.
 * Returns canonical UTC ISO for sort/seek/selection, or null when not resolvable here.
 */
export function exploreCanonicalUtcIso(
  utc: string | null | undefined,
  display: string | null | undefined
): string | null {
  const fromUtc = parseExploreUtcIso(utc);
  if (fromUtc) {return fromUtc;}
  const displayRaw = String(display ?? '').trim();
  if (!displayRaw) {return null;}
  if (ISO_WITH_TZ_SUFFIX_RE.test(displayRaw)) {
    return parseExploreUtcIso(displayRaw);
  }
  return null;
}

function parseMergedPayload(data: unknown): ExploreTagMergedRow[] {
  if (data == null) {return [];}
  if (Array.isArray(data)) {return data as ExploreTagMergedRow[];}
  if (typeof data === 'object' && data !== null && Array.isArray((data as { rows?: unknown }).rows)) {
    return (data as { rows: ExploreTagMergedRow[] }).rows;
  }
  return [];
}

/** Fleet day uses YYYYMMDD; explore API expects YYYY-MM-DD. */
export function normalizeExploreTargetDate(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim();
  if (!s) {return null;}
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {return s;}
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {return d.toISOString().slice(0, 10);}
  return null;
}

export function exploreRowKind(row: ExploreTagMergedRow): 'user_event' | 'dataset_event' | '' {
  const k = String(row.kind ?? '').trim().toLowerCase();
  if (k === 'user_event' || k === 'dataset_event') {return k;}
  if (row.event_id != null && row.user_event_id == null) {return 'dataset_event';}
  if (row.user_event_id != null) {return 'user_event';}
  return '';
}

export async function fetchExploreTagRows(args: {
  className?: string | null;
  projectId: number,
  datasetId?: number | null,
  targetYmd?: string | null,
  timezone?: string | null,
  signal?: AbortSignal
}): Promise<ExploreTagMergedRow[]> {
  const cn = String(args.className || '').trim();
  const datasetId = Number(args.datasetId);
  const hasDataset = Number.isFinite(datasetId) && datasetId > 0;
  const targetYmd = normalizeExploreTargetDate(args.targetYmd);
  if (!Number.isFinite(args.projectId) || args.projectId <= 0) {
    return [];
  }
  if (!cn) {
    warn('[userEventsExploreService] Missing className for explore tags (dataset_events require schema)');
    return [];
  }
  if (hasDataset && !Number.isFinite(datasetId)) {
    return [];
  }
  if (!hasDataset && !targetYmd) {
    return [];
  }
  const params = new URLSearchParams();
  params.set('project_id', String(args.projectId));
  const tz = String(args.timezone ?? '').trim();
  params.set('timezone', tz || 'UTC');
  // class_name is required for dataset_events UNION (ac40.dataset_events), not only for dataset_id mode
  params.set('class_name', cn);
  if (hasDataset) {
    params.set('dataset_id', String(datasetId));
  } else {
    params.set('target_date', targetYmd!);
  }
  const url = `${apiEndpoints.app.userEvents}/explore?${params.toString()}`;
  const res = await getData(url, args.signal);
  if (!res.success) {
    if (res.type === 'AbortError' || res.message === 'Request cancelled') {
      debug('[userEventsExploreService] fetchExploreTagRows aborted (superseded by newer load)', {
        projectId: args.projectId,
        datasetId: args.datasetId ?? null,
      });
      return [];
    }
    warn('[userEventsExploreService] fetchExploreTagRows failed', res.message);
    return [];
  }
  const rows = parseMergedPayload(res.data);
  const byKind: Record<string, number> = {};
  for (const row of rows) {
    const k = exploreRowKind(row) || 'unknown';
    byKind[k] = (byKind[k] || 0) + 1;
  }
  debug('[userEventsExploreService] Loaded merged explore tag rows', {
    count: rows.length,
    byKind,
    hasDataset,
    targetYmd: hasDataset ? null : targetYmd,
    datasetId: hasDataset ? datasetId : null,
    url,
  });
  return rows;
}

function mergeTagsForPut(
  _eventType: string,
  existing: Record<string, unknown> | null | undefined,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const base =
    existing != null && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...existing }
      : {};
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) {
      delete out[k];
      continue;
    }
    out[k] = v;
  }
  return out;
}

export async function putUserEventExploreUpdate(
  projectId: number,
  row: ExploreUserEventRow,
  tagsPatch: Record<string, unknown>,
  timeOverrides?: Partial<
    Pick<ExploreUserEventRow, 'date' | 'focus_time' | 'start_time' | 'end_time'>
  >
): Promise<ExploreUserEventRow | null> {
  const id = row.user_event_id;
  if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(projectId) || projectId <= 0) {
    return null;
  }
  const tags = mergeTagsForPut(row.event_type, row.tags ?? undefined, tagsPatch);
  const body: Record<string, unknown> = {
    project_id: projectId,
    date: timeOverrides?.date ?? row.date ?? null,
    focus_time: timeOverrides?.focus_time ?? row.focus_time ?? null,
    start_time: timeOverrides?.start_time ?? row.start_time ?? null,
    end_time: timeOverrides?.end_time ?? row.end_time ?? null,
    event_type: row.event_type,
    tags,
  };
  const url = `${apiEndpoints.app.userEvents}/${id}`;
  const res = await putData(url, body);
  if (!res.success) {
    warn('[userEventsExploreService] PUT failed', res.message);
    return null;
  }
  const data = res.data as ExploreUserEventRow | undefined;
  if (data && typeof data === 'object' && 'user_event_id' in data) {
    return data as ExploreUserEventRow;
  }
  return { ...row, tags };
}
